import { useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { selectActiveLeadsType, selectIsAggregatedDataUsed } from "app/pages/sales-intelligence/modules/leads-search";
import ExportSelectTrafficCountryModalContainer from "app/pages/sales-intelligence/modules/leads-search/containers/leads-actions/ExportSelectTrafficCountryModal";
import { useCrmLeadsExportPanel } from "app/pages/sales-intelligence/modules/leads-search/hooks/useCrmLeadsExportPanel";
import { selectCountriesFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import LegacyWebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/LegacyWebsitesCRMExportPanel/LegacyWebsitesCRMExportPanelView";
import WebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/WebsitesCRMExportPanel/WebsitesCRMExportPanelView";
import { useExportCountryModal } from "app/pages/sales-intelligence/sub-modules/management/hooks/useExportCountryModal";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { Translatable } from "app/pages/sales-intelligence/types";

import { useLeadsResultsActionsState } from "../../../query/queries/useLeadsResultsActionsState";

type Props = BaseActivatedPanelProps & {
    integration: Integration;
    isExportInProgress: boolean;
    label: Readonly<Translatable>;
    onExportNDomains: (domainsCount: number, countries: readonly number[]) => void;
    onExportDomains: (domains: readonly string[], countries: readonly number[]) => void;
};

const CrmExportPanelContainer = ({
    integration,
    isExportInProgress,
    label,
    onExportNDomains,
    onExportDomains,
    onDeactivate,
}: Props) => {
    const activeLeadsType = useSelector(selectActiveLeadsType);
    const isAggregatedDataUsed = useSelector(selectIsAggregatedDataUsed);
    const countriesFilterValue = useSelector(selectCountriesFilterValue);

    const { excelQuota, selectedRowDomains, leadsSelectionDropdown, onClearAndDeactivate, onSelectionChange } =
        useCrmLeadsExportPanel(onDeactivate);

    const { areActionsDisabled, numberOfResults } = useLeadsResultsActionsState(activeLeadsType);

    const handleExport = (selectionItem: Readonly<SelectionDropdownItem>, countries: readonly number[]): void => {
        onClearAndDeactivate();

        if (selectionItem.id === ManualSelectionItem.ID) {
            return onExportDomains(selectedRowDomains, countries);
        }

        return onExportNDomains(selectionItem.value, countries);
    };

    const { modalSelectionItem, onModalOpen, onModalSubmit, onModalClose } =
        useExportCountryModal<SelectionDropdownItem>((selectionItem, countryId) => {
            handleExport(selectionItem, [countryId]);
        });

    const handleSubmit = (item: Readonly<SelectionDropdownItem>): void => {
        trackingService.trackMultiSelectorExportClick(integration, excelQuota.remaining, item.alias, item.value);

        if (isAggregatedDataUsed || countriesFilterValue?.length === 1) {
            handleExport(item, countriesFilterValue ?? []);
            return;
        }

        onModalOpen(item);
    };

    return (
        <>
            <ExportSelectTrafficCountryModalContainer
                integration={integration}
                isOpen={modalSelectionItem !== null}
                onCancel={onModalClose}
                onExport={onModalSubmit}
            />
            {salesSettingsHelper.isDataCreditsEnabled ? (
                <WebsitesCRMExportPanelView
                    isDisabled={isExportInProgress || areActionsDisabled}
                    isLoading={isExportInProgress}
                    label={label}
                    numberOfSelectedItems={selectedRowDomains.length}
                    numberOfRemainingExports={excelQuota.remaining}
                    numberOfResults={numberOfResults}
                    selectionDropdown={leadsSelectionDropdown}
                    onDeactivate={onClearAndDeactivate}
                    onSelectionChange={onSelectionChange}
                    onSubmit={handleSubmit}
                    exportType={
                        integration === "hubspot"
                            ? ExportCostTypeItem.Hubspot
                            : integration === "dynamics"
                              ? ExportCostTypeItem.Dynamics
                              : ExportCostTypeItem.Salesforce
                    }
                />
            ) : (
                <LegacyWebsitesCRMExportPanelView
                    isDisabled={isExportInProgress || areActionsDisabled}
                    isLoading={isExportInProgress}
                    label={label}
                    numberOfSelectedItems={selectedRowDomains.length}
                    numberOfRemainingExports={excelQuota.remaining}
                    numberOfResults={numberOfResults}
                    selectionDropdown={leadsSelectionDropdown}
                    onDeactivate={onClearAndDeactivate}
                    onSelectionChange={onSelectionChange}
                    onSubmit={handleSubmit}
                />
            )}
        </>
    );
};

export default CrmExportPanelContainer;
